"use client";

import { useState, FormEvent } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section id="contact" className="py-32 px-6 bg-black border-t border-border">
      <div className="max-w-4xl mx-auto text-center space-y-16">
        {/* Header */}
        <div className="space-y-6">
          <span className="label-micro text-primary">03 // GET IN TOUCH</span>
          <h2 className="text-5xl md:text-8xl font-bold tracking-tighter uppercase leading-none">
            Let's Talk <br />
            <span className="text-primary">Technology.</span>
          </h2>
          <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto">
            Schedule a free consultation. No obligation, no pressure. 
            Let's discuss how we can help your business grow and thrive.
          </p>
        </div>

        {/* Contact Info */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mx-auto mb-4">
              <Icon name="PhoneIcon" size={20} />
            </div>
            <p className="label-micro text-muted-foreground">Phone</p>
            <p className="text-sm font-bold text-white">(916) 490-9862</p>
          </div>
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mx-auto mb-4">
              <Icon name="EnvelopeIcon" size={20} />
            </div>
            <p className="label-micro text-muted-foreground">Email</p>
            <p className="text-sm font-bold text-white">support@armadamsp.net</p>
          </div>
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mx-auto mb-4">
              <Icon name="MapPinIcon" size={20} />
            </div>
            <p className="label-micro text-muted-foreground">Location</p>
            <p className="text-sm font-bold text-white">1630 Terminal St<br />West Sacramento, CA 95691</p>
          </div>
        </div>

        {/* Form */}
        {isSubmitted ? (
          <div className="card-glass rounded-[40px] p-12 space-y-6 animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-success/10 border border-success/20 flex items-center justify-center text-success mx-auto">
              <Icon name="CheckCircleIcon" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-white">Thank You!</h3>
            <p className="text-muted-foreground font-mono text-sm">
              We've received your message and will get back to you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 text-left">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="name" className="label-micro text-muted-foreground block">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-white/20 pb-3 text-sm text-white focus:outline-none focus:border-primary transition-colors"
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="label-micro text-muted-foreground block">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-white/20 pb-3 text-sm text-white focus:outline-none focus:border-primary transition-colors"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="company" className="label-micro text-muted-foreground block">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 pb-3 text-sm text-white focus:outline-none focus:border-primary transition-colors"
                  placeholder="Acme Corp"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="phone" className="label-micro text-muted-foreground block">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 pb-3 text-sm text-white focus:outline-none focus:border-primary transition-colors"
                  placeholder="(916) 555-0100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="message" className="label-micro text-muted-foreground block">
                How Can We Help? *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="w-full bg-transparent border border-white/20 rounded-lg p-4 text-sm text-white focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Tell us about your IT needs..."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-6 pt-6">
              <button
                type="submit"
                className="px-12 py-4 bg-primary text-primary-foreground rounded-full label-micro hover:scale-105 transition-all btn-glow"
              >
                Send Message
              </button>
              <p className="text-xs text-muted-foreground self-center">
                Free consultation • No obligation
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}